<template>
  <net-popup-menu @close="emit('close')">
    <template #title>{{ $t("objectNames.stakeholder") }}</template>

    <input-text
      ref="titleField"
      :prevent-submit="true"
      :value="ctx.stakeholder.title"
      :label="$t('labels.title')"
      :placeholder="$t('labels.title')"
      @enter="focusDescription()"
      @update:value="ctx.updateKey('title', $event)"
    />
    <input-texteditor
      ref="descriptionField"
      :prevent-submit="true"
      :value="ctx.stakeholder.description"
      :editor="true"
      :label="$t('labels.description')"
      :placeholder="$t('labels.description')"
      @update:value="ctx.updateKey('description', $event)"
    />

    <div
      v-if="features.target"
    >
      <h3 class="label mt-0">{{ $t("objectNames.target", 3) }}</h3>
      <button-list
        :items="targets"
        :order="targetsOrder"
        :removable="true"
        :transition-active="false"
        prop="title"
        @select="selectTarget"
        @remove="removeTarget"
      >
        <template #after-list>
          <item-combo-select
            key="stakeholder-list-targetselect"
            :available-ids="{
              target: targetsOrder,
              resource: [],
              index: []
            }"
            :targets="allTargets"
            :resources="allResources"
            :indices="allIndices"
            :selection="targets"
            :hide-selected="true"
            :types="['target']"
            @select-single="addTarget"
            @choose="onTargetsChosen"
          />
        </template>
      </button-list>
    </div>

    <div
      v-if="features.action"
    >
      <h3 class="label mt-0">{{ $t("objectNames.action", 3) }}</h3>
      <button-list
        :items="actions"
        :order="actionsOrder"
        :removable="true"
        :transition-active="false"
        prop="title"
        @select="selectAction"
        @remove="removeAction"
      >
        <template #after-list>
          <input-dropdown
            key="stakeholder-menu-actiondropdown"
            :options="availableActions"
            :order="actionsOrder"
            :clear-on-select="true"
            :searchable="true"
            prop="title"
            :empty-text="$t('errors.noEntries')"
            @select="addAction($event.item)"
          />
        </template>
      </button-list>
    </div>

    <div
      v-if="features.resource"
    >
      <h3 class="label mt-0">{{ $t("objectNames.resource", 3) }}</h3>
      <button-list
        :items="resources"
        :order="resourceOrder"
        :removable="true"
        :transition-active="false"
        prop="title"
        @select="selectResource"
        @remove="removeResource"
      >
        <template #after-list>
          <item-combo-select
            key="stakeholder-list-resourceselect"
            :available-ids="{
              target: [],
              resource: resourceOrder,
              index: []
            }"
            :targets="allTargets"
            :resources="allResources"
            :indices="allIndices"
            :selection="resources"
            :hide-selected="true"
            :types="['resource']"
            @select-single="addResource"
            @choose="onResourcesChosen"
          />
        </template>
      </button-list>
    </div>

    <div
      v-if="features.index"
    >
      <h3 class="label mt-0">{{ $t("objectNames.index", 3) }}</h3>
      <button-list
        :items="indices"
        :order="indexOrder"
        :removable="true"
        :transition-active="false"
        prop="title"
        @select="selectIndex"
        @remove="removeIndex"
      >
        <template #after-list>
          <item-combo-select
            key="stakeholder-list-indexselect"
            :available-ids="{
              target: [],
              resource: [],
              index: indexOrder
            }"
            :targets="allTargets"
            :resources="allResources"
            :indices="allIndices"
            :selection="indices"
            :hide-selected="true"
            :types="['index']"
            @select-single="addIndex"
            @choose="onIndicesChosen"
          />
        </template>
      </button-list>
    </div>

    <button
      class="button button_secondary mt-4"
      type="button"
      @click="deleteStakeholder"
    >
      {{ $t("actions.deleteStakeholder") }}
    </button>
  </net-popup-menu>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, ref, nextTick } from "vue";
import { useRouter } from "vue-router";
import { without } from "lodash-es";

import NetPopupMenu from '@editor/net/NetPopupMenu.vue';
import InputDropdown from "@core/input/InputDropdown.vue";
import InputText from "@core/input/InputText.vue";
import InputTexteditor from "@core/input/TextEditor.vue";
import ButtonList from "@core/ui/ButtonList.vue";

import ItemComboSelect from "@editor/ui/ItemComboSelect.vue";

import { useFeatures } from "@editor/store/features.store";
import { useStakeholder } from "./useStakeholder";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useStakeholderStore } from "@editor/items/stakeholder/stakeholders.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useVariablesViewStore } from "@editor/views/variables/variablesView.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";

import type { ActionItem } from "@editor/items/action/ActionItem";
import type { TargetItem} from "@editor/items/target/TargetItem";
import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { IndexItem } from "@editor/items/index/IndexItem";
import type { Uid } from "@/helpers/utility/Id";

const props = defineProps<{
  stakeholderId: number;
}>();

const emit = defineEmits<{
  (e: "select", item: { uid: Uid<"stakeholder"> }): void;
  (e: "close"): void;
}>();

const router = useRouter();
const features = useFeatures();
const openDrawers = useDrawerStates();
const selectionStore = useSelectionStore();

const titleField = ref<HTMLInputElement>();
const descriptionField = ref<HTMLTextAreaElement>();

const ctx = useStakeholder(() => props.stakeholderId);

// ======================
// Targets

const targetStore = useTargetStore();

const allTargets = computed(() => {
  return targetStore.items;
});
const targets = computed(() => {
  return targetStore.getArrayByIds(ctx.stakeholder?.targetIds || []);
});
const targetsOrder = computed(() => {
  return targetStore.order;
});

function selectTarget(target: TargetItem) {
  router.push({ path: "model" });

  openDrawers.targetStakeholders = true;
  selectionStore.select(target);
}
function onTargetsChosen(targets: { type: "target"; id: number }[]) {
  if (ctx.stakeholder == null) throw new Error("No stakeholder");

  const ids = targets.map((target) => target.id);
  ctx.update({
    targetIds: ids
  });
}
function addTarget(target: { type: "target"; id: number } | null) {
  if (target == null) return;
  if (ctx.stakeholder == null) throw new Error("No stakeholder");

  if (ctx.stakeholder.targetIds.includes(target.id)) {
    return;
  }
  ctx.update({
    targetIds: [...ctx.stakeholder.targetIds, target.id]
  });
}
function removeTarget(target: { type: "target"; id: number } | null) {
  if (target == null) return;
  if (ctx.stakeholder == null) throw new Error("No stakeholder");

  const ids = ctx.stakeholder.targetIds.filter(
    (id) => id != null && id != target.id
  );
  ctx.update({
    targetIds: ids
  });
}

// ======================
// Resources

const resourceStore = useResourceStore();

const allResources = computed(() => {
  return resourceStore.items;
});
const resources = computed(() => {
  if (ctx.stakeholder == null) return [];
  return resourceStore.getArrayByIds(ctx.stakeholder.resourceIds);
});
const resourceOrder = computed(() => {
  return resourceStore.order;
});

function selectResource(resource: ResourceItem) {
  router.push({ path: "model" });

  openDrawers.resourceStakeholders = true;
  selectionStore.select(resource);
}
function onResourcesChosen(resources: { type: "resource"; id: number }[]) {
  if (ctx.stakeholder == null) throw new Error("No stakeholder");

  const ids = resources.map((resource) => resource.id);
  ctx.update({
    resourceIds: ids
  });
}
function addResource(resource: { type: "resource"; id: number } | null) {
  if (resource == null) return;
  if (ctx.stakeholder == null) throw new Error("No stakeholder");

  if (ctx.stakeholder.resourceIds.includes(resource.id)) {
    return;
  }
  ctx.update({
    resourceIds: [...ctx.stakeholder.resourceIds, resource.id]
  });
}
function removeResource(resource: { type: "resource"; id: number } | null) {
  if (resource == null) return;
  if (ctx.stakeholder == null) throw new Error("No stakeholder");

  const resourceIds = ctx.stakeholder.resourceIds.filter(
    (id) => id != null && id != resource.id
  );
  ctx.updateKey("resourceIds", resourceIds);
}


// ======================
// Indices

const indexStore = useIndexStore();

const allIndices = computed(() => {
  return indexStore.items;
});
const indices = computed(() => {
  if (ctx.stakeholder == null) return [];
  return indexStore.getArrayByIds(ctx.stakeholder.indexIds);
});
const indexOrder = computed(() => {
  return indexStore.order;
});

function selectIndex(index: IndexItem) {
  router.push({ path: "model" });

  openDrawers.indexStakeholders = true;
  selectionStore.select(index);
}
function onIndicesChosen(indices: { type: "index"; id: number }[]) {
  if (ctx.stakeholder == null) throw new Error("No stakeholder");

  const ids = indices.map((index) => index.id);
  ctx.update({
    indexIds: ids
  });
}
function addIndex(index: { type: "index"; id: number } | null) {
  if (index == null) return;
  if (ctx.stakeholder == null) throw new Error("No stakeholder");

  if (ctx.stakeholder.indexIds.includes(index.id)) {
    return;
  }
  ctx.update({
    indexIds: [...ctx.stakeholder.indexIds, index.id]
  });
}
function removeIndex(index: { type: "index"; id: number } | null) {
  if (index == null) return;
  if (ctx.stakeholder == null) throw new Error("No stakeholder");

  const indexIds = ctx.stakeholder.indexIds.filter(
    (id) => id != null && id != index.id
  );
  ctx.updateKey("indexIds", indexIds);
}

// ======================
// Actions

const actionStore = useActionStore();

const actions = computed(() => {
  if (ctx.stakeholder == null) return [];
  return actionStore.getArrayByIds(ctx.stakeholder.actionIds);
});
const availableActions = computed(() => {
  const allActions = actionStore.orderedItems;
  return without(allActions, ...actions.value);
});
const actionsOrder = computed(() => {
  return actionStore.order;
});

function selectAction(action: ActionItem) {
  router.push({ path: "variables" });
  const variablesView = useVariablesViewStore();
  variablesView.selectMain(action);
  variablesView.selectSub(ctx.stakeholder);
}

function addAction(action: ActionItem) {
  if (ctx.stakeholder == null) throw new Error("No stakeholder");

  const newActionIds = ctx.stakeholder.actionIds.concat([action.id]);
  ctx.updateKey("actionIds", newActionIds);
}
function removeAction(action: ActionItem) {
  if (ctx.stakeholder == null) throw new Error("No stakeholder");

  ctx.updateKey("actionIds", without(ctx.stakeholder.actionIds, action.id));
}

// ======================
// Delete

function deleteStakeholder() {
  if (ctx.stakeholder == null) throw new Error("No stakeholder");

  const stakeholder = ctx.stakeholder;
  emit("close");
  return useStakeholderStore().delete(stakeholder);
}

// ======================
// Focus Management

watch(
  () => props.stakeholderId,
  () => {
    if (ctx.stakeholder == null) return;
    if (ctx.stakeholder.title === "") {
      focusTitle();
    }
  }
);

onMounted(() => {
  if (ctx.stakeholder?.title === "") {
    focusTitle();
  }
});

function focusTitle() {
  openDrawers.stakeholderGeneral = true;
  nextTick(() => {
    titleField.value?.focus();
  });
}
function focusDescription() {
  openDrawers.stakeholderGeneral = true;
  nextTick(() => {
    descriptionField.value?.focus();
  });
}
</script>